/**
 * Grammar & Spell Check — client-side entity executor.
 *
 * A non-AI checker that runs entirely in the user's browser: spelling via
 * `retext-spell` (nspell + an inlined English Hunspell dictionary) plus
 * grammar/style/readability via the `retext`/unified collective. The
 * document text never reaches the server; there is no provider call, no API
 * key, no per-call cost. Exported as a definition object (entity config in
 * `packages/entities/src/integrations/grammar-check/config.ts`); the webapp
 * registers it at startup via
 * `client/src/lib/register-client-entities.ts`.
 *
 * The engine (the retext processor + the ~0.5 MB dictionary) is dynamically
 * imported and memoized so it is code-split out of the main client bundle and
 * the Hunspell dictionary is parsed exactly once. The 100k-character input cap
 * is enforced here rather than in the bridge/registry — per-entity input
 * constraints are the executor's responsibility.
 */

import type { ClientEntityDefinition } from './types';

/**
 * Entity ID of the grammar_check client-side entity. Literal here so the
 * client never imports `@fias/entities` (whose server-only transitive deps
 * would bloat the bundle).
 *
 * SYNC NOTE: must match `GRAMMAR_CHECK_ENTITY_ID` in
 * `packages/entities/src/integrations/grammar-check/config.ts`. The
 * architecture test `client-entity-registry-mirrors-entity-config.test.ts`
 * enforces this.
 */
export const GRAMMAR_CHECK_ENTITY_ID = 'ent_intg_grammar_check_000000000';

/** Maximum input size. Matches the SDK-side precheck. */
export const GRAMMAR_CHECK_MAX_CHARS = 100_000;

/**
 * Cap on `customDictionary` entries. Plugin payloads cross the bridge with
 * their TypeScript types erased, so an over-large array is bounded here to
 * keep a buggy/hostile arche from exhausting the user's tab memory.
 */
export const GRAMMAR_CHECK_MAX_CUSTOM_DICTIONARY = 10_000;

export type GrammarCategory = 'spelling' | 'grammar' | 'style' | 'readability';

export const GRAMMAR_CATEGORIES: readonly GrammarCategory[] = [
  'spelling',
  'grammar',
  'style',
  'readability',
];

export interface GrammarCheckInput {
  /** The text to check. */
  text: string;
  /** Categories to report. Omitted/empty means all categories. */
  categories?: GrammarCategory[];
  /** Words the user has added to their personal dictionary (never flagged as misspelled). */
  customDictionary?: string[];
}

export interface GrammarIssue {
  /** Inclusive start character offset into the submitted text. */
  start: number;
  /** Exclusive end character offset into the submitted text. */
  end: number;
  /** Human-readable description of the issue. */
  message: string;
  category: GrammarCategory;
  /** Stable rule identifier, e.g. `retext-spell:colour`. */
  ruleId: string;
  /** Suggested replacements (may be empty). */
  suggestions: string[];
}

export interface GrammarReadability {
  /** Number of sentences flagged as hard to read. */
  hardToReadSentences: number;
}

export interface GrammarCheckResult {
  issues: GrammarIssue[];
  readability?: GrammarReadability;
}

/** Maps a retext plugin's `source` to the issue category we surface. */
const SOURCE_CATEGORY: Readonly<Record<string, GrammarCategory>> = {
  'retext-spell': 'spelling',
  'retext-repeated-words': 'grammar',
  'retext-indefinite-article': 'grammar',
  'retext-redundant-acronyms': 'grammar',
  'retext-contractions': 'grammar',
  'retext-sentence-spacing': 'grammar',
  'retext-quotes': 'grammar',
  'retext-passive': 'style',
  'retext-simplify': 'style',
  'retext-readability': 'readability',
};

/** Minimal shape of the messages a retext processor produces (vfile-message). */
interface RetextMessage {
  reason: string;
  source?: string | null;
  ruleId?: string | null;
  actual?: string | null;
  expected?: ReadonlyArray<string> | null;
  place?: unknown;
  position?: unknown;
}

interface RetextProcessor {
  process: (text: string) => Promise<{ messages: ReadonlyArray<RetextMessage> }>;
}

let enginePromise: Promise<RetextProcessor> | null = null;

/**
 * Build (once) a retext processor with every supported rule registered. We
 * register all plugins and filter results by the requested categories at
 * call time rather than rebuilding per request — that keeps the Hunspell
 * dictionary parse (the expensive step) cached across invocations.
 */
function loadEngine(): Promise<RetextProcessor> {
  if (!enginePromise) {
    enginePromise = (async () => {
      const [
        retextMod,
        spell,
        repeatedWords,
        indefiniteArticle,
        redundantAcronyms,
        contractions,
        sentenceSpacing,
        quotes,
        passive,
        readability,
        simplify,
        dict,
      ] = await Promise.all([
        import('retext'),
        import('retext-spell'),
        import('retext-repeated-words'),
        import('retext-indefinite-article'),
        import('retext-redundant-acronyms'),
        import('retext-contractions'),
        import('retext-sentence-spacing'),
        import('retext-quotes'),
        import('retext-passive'),
        import('retext-readability'),
        import('retext-simplify'),
        import('./grammar-dictionary.generated'),
      ]);

      // `dictionary` is honestly typed as { aff: string; dic: string } (what
      // nspell consumes). retext-spell's option type narrows aff/dic to
      // Uint8Array, so the assertion is scoped to this one call site where the
      // upstream type is too strict — the value itself is never mistyped.
      const dictionary = dict.getDictionary();
      const processor = retextMod
        .retext()
        .use(spell.default, {
          dictionary: dictionary as unknown as { aff: Uint8Array; dic: Uint8Array },
        })
        .use(repeatedWords.default)
        .use(indefiniteArticle.default)
        .use(redundantAcronyms.default)
        .use(contractions.default)
        .use(sentenceSpacing.default)
        .use(quotes.default)
        .use(passive.default)
        .use(readability.default)
        .use(simplify.default);

      return processor as unknown as RetextProcessor;
    })().catch((err: unknown) => {
      // Clear the cached rejected promise so a transient chunk-load failure
      // retries from scratch on the next call instead of forever re-throwing.
      enginePromise = null;
      throw err;
    });
  }
  return enginePromise;
}

/**
 * Extract `{ start, end }` character offsets from a retext message's
 * position, tolerating both the `place` (vfile-message v4) and legacy
 * `position` fields and both Point and Position shapes.
 */
function offsetsOf(message: RetextMessage): { start: number; end: number } | null {
  const loc = (message.place ?? message.position) as
    | { start?: { offset?: number }; end?: { offset?: number }; offset?: number }
    | null
    | undefined;
  if (!loc) return null;
  if (typeof loc.offset === 'number') {
    return { start: loc.offset, end: loc.offset };
  }
  const start = loc.start?.offset;
  const end = loc.end?.offset;
  if (typeof start === 'number' && typeof end === 'number') {
    return { start, end };
  }
  return null;
}

// retext processing reuses the memoized nspell checker; serialize calls on a
// tail promise so concurrent invocations (internal arche + plugin bridge)
// don't race the checker's lazily-built internal caches. The `.catch(() => {})`
// keeps one failed call from poisoning the chain.
let tail: Promise<void> = Promise.resolve();

export async function grammarCheckExecutor(input: GrammarCheckInput): Promise<GrammarCheckResult> {
  if (!input || typeof input.text !== 'string') {
    throw new Error('grammar-check: input.text must be a string');
  }
  if (input.text.length > GRAMMAR_CHECK_MAX_CHARS) {
    throw new Error(
      `grammar-check: text exceeds ${GRAMMAR_CHECK_MAX_CHARS}-character limit (got ${input.text.length})`,
    );
  }

  // Normalize the optional fields defensively — plugin payloads arrive over the
  // bridge with their TS types erased, so guard against non-arrays / non-strings
  // rather than letting `.map`/`.has` throw an opaque error deep in the engine.
  const requestedCategories = Array.isArray(input.categories)
    ? input.categories.filter((c): c is GrammarCategory =>
        (GRAMMAR_CATEGORIES as readonly string[]).includes(c as string),
      )
    : [];
  const enabled = new Set<GrammarCategory>(
    requestedCategories.length > 0 ? requestedCategories : GRAMMAR_CATEGORIES,
  );

  const rawCustom = Array.isArray(input.customDictionary) ? input.customDictionary : [];
  if (rawCustom.length > GRAMMAR_CHECK_MAX_CUSTOM_DICTIONARY) {
    throw new Error(
      `grammar-check: customDictionary exceeds ${GRAMMAR_CHECK_MAX_CUSTOM_DICTIONARY}-entry limit (got ${rawCustom.length})`,
    );
  }
  const custom = new Set(
    rawCustom.filter((w): w is string => typeof w === 'string').map((w) => w.toLowerCase()),
  );

  const run = tail.then(async () => {
    const processor = await loadEngine();
    const file = await processor.process(input.text);
    return file.messages;
  });
  tail = run.then(
    () => {},
    () => {},
  );
  const messages = await run;

  const issues: GrammarIssue[] = [];
  for (const message of messages) {
    const source = message.source ?? '';
    const category = SOURCE_CATEGORY[source];
    if (!category || !enabled.has(category)) continue;

    const offsets = offsetsOf(message);
    if (!offsets) continue;

    const actual = message.actual ?? input.text.slice(offsets.start, offsets.end);
    // A word the user added to their personal dictionary is never a misspelling.
    if (category === 'spelling' && custom.has(actual.toLowerCase())) continue;

    issues.push({
      start: offsets.start,
      end: offsets.end,
      message: message.reason,
      category,
      ruleId: message.ruleId ? `${source}:${message.ruleId}` : source,
      suggestions: Array.isArray(message.expected)
        ? message.expected.filter((s): s is string => typeof s === 'string')
        : [],
    });
  }

  issues.sort((a, b) => a.start - b.start || a.end - b.end);

  const result: GrammarCheckResult = { issues };
  if (enabled.has('readability')) {
    result.readability = {
      hardToReadSentences: issues.filter((i) => i.category === 'readability').length,
    };
  }
  return result;
}

/**
 * Warm the engine (dictionary download + parse) so the first user-triggered
 * check avoids the load latency. No-op if already loaded.
 */
export async function prefetchGrammarCheck(): Promise<void> {
  await loadEngine();
}

/**
 * Registration input for the webapp's client-entity registry. Rate limit and
 * permission must mirror `grammarCheckConfig.pluginInvocable` in
 * `@fias/entities` (pinned by
 * `client-entity-registry-mirrors-entity-config.test.ts`).
 */
export const grammarCheckDefinition = {
  entityId: GRAMMAR_CHECK_ENTITY_ID,
  rateLimitPerMinute: 30,
  // Generic client-side-entity permission.
  pluginPermission: 'entities:client_invoke',
  executor: grammarCheckExecutor,
} as const satisfies ClientEntityDefinition<GrammarCheckInput, GrammarCheckResult>;
